/* music-keymap.js — shared computer-keyboard → note mapping for the instrument tools.
 *
 * Why a module and not another copy-paste: /organ, /online-piano and /flute each
 * carried the SAME 17-entry KEYMAP literal, and each one shipped the same two
 * defects — the on-key letter hints rendered at ~1.5:1 contrast (invisible), and
 * Shift+letter silently played the NATURAL instead of the sharp.
 *
 * Two mapping schools exist on the web and both are real conventions:
 *
 *   'daw'        Ableton Live / Logic / GarageBand "Musical Typing", Musicca.
 *                Home row = white keys, row above = black keys, laid out with the
 *                same geometry as a piano. ~1.4 octaves. Transfers to a real
 *                instrument: a-d-g really is the shape of a C major triad.
 *
 *   'sequential' virtualpiano.net / multiplayerpiano. The number+letter rows run
 *                straight up the WHITE keys (36 of them = exactly a 61-key manual),
 *                Shift = the black key above. No octave shifting needed to play a
 *                whole song, and it is the notation every community "letter sheet"
 *                on the internet is written in.
 *
 * Measured 2026-08-02 (60d, RU shard, /organ): our own documented Shift+←/→ was
 * used by 23 sessions out of 928 that played (2.5%), while Shift+<letter> — the
 * virtualpiano habit, which we answered with a wrong note — arrived in 31. More
 * people brought the other convention to us than used ours. Hence: support both.
 *
 * Shift rule (unified across both schemes): Shift = the black key immediately
 * above. If there is none (E, B), nothing sounds. Never a wrong natural.
 */
(function (w) {
    'use strict';

    var NOTE_NAMES = ['C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'B'];
    var BLACK = { 1: 1, 3: 1, 6: 1, 8: 1, 10: 1 };
    function isBlack(m) { return !!BLACK[((m % 12) + 12) % 12]; }
    function noteName(m) { return NOTE_NAMES[((m % 12) + 12) % 12] + (Math.floor(m / 12) - 1); }

    /* ── scheme 'daw': semitone offset from a movable base note ── */
    var DAW_OFFSETS = {
        KeyA: 0, KeyW: 1, KeyS: 2, KeyE: 3, KeyD: 4, KeyF: 5, KeyT: 6, KeyG: 7,
        KeyY: 8, KeyH: 9, KeyU: 10, KeyJ: 11, KeyK: 12, KeyO: 13, KeyL: 14,
        KeyP: 15, Semicolon: 16
    };

    /* ── scheme 'sequential': nth WHITE key of whatever is on screen ──
       Row order is the virtualpiano order: numbers, then qwerty, then asdf, then zxcv.
       10 + 10 + 9 + 7 = 36 = the white-key count of a 61-key C2–C7 manual. */
    var SEQ_CODES = [
        'Digit1', 'Digit2', 'Digit3', 'Digit4', 'Digit5', 'Digit6', 'Digit7', 'Digit8', 'Digit9', 'Digit0',
        'KeyQ', 'KeyW', 'KeyE', 'KeyR', 'KeyT', 'KeyY', 'KeyU', 'KeyI', 'KeyO', 'KeyP',
        'KeyA', 'KeyS', 'KeyD', 'KeyF', 'KeyG', 'KeyH', 'KeyJ', 'KeyK', 'KeyL',
        'KeyZ', 'KeyX', 'KeyC', 'KeyV', 'KeyB', 'KeyN', 'KeyM'
    ];
    var SEQ_INDEX = {};
    for (var si = 0; si < SEQ_CODES.length; si++) SEQ_INDEX[SEQ_CODES[si]] = si;

    /* Pretty label for a physical key code — what we paint on the on-screen key. */
    function codeLabel(code) {
        if (code.indexOf('Key') === 0) return code.slice(3);
        if (code.indexOf('Digit') === 0) return code.slice(5);
        if (code === 'Semicolon') return ';';
        return code;
    }

    function create(opts) {
        opts = opts || {};

        var scheme = opts.scheme === 'sequential' ? 'sequential' : 'daw';
        var base = typeof opts.base === 'number' ? opts.base : 60;   /* daw only */
        var baseMin = typeof opts.baseMin === 'number' ? opts.baseMin : 36;
        var baseMax = typeof opts.baseMax === 'number' ? opts.baseMax : 84;
        var whites = [];        /* MIDI numbers of the white keys currently on screen */
        var pedalBase = null;   /* organ only — bottom row becomes a pedalboard */

        /* rollover: how many keys the user's hardware actually reports at once.
           Cheap membranes ghost at 3–6; this is the real reason a held chord
           "loses" a note and nothing on the web ever explains it. */
        var held = {};
        var heldN = 0, maxHeld = 0;

        function clampBase(v) { return Math.max(baseMin, Math.min(baseMax, v)); }

        /* Black key immediately above `m`, or null if there isn't one (E, B). */
        function sharpAbove(m) { return isBlack(m + 1) ? m + 1 : null; }

        /* zxcvbnm is the pedalboard when the tool asked for one. It is free in BOTH
           schemes (the daw row stops at ';', the sequential row ends before z when
           fewer than 30 white keys are on screen), so an organist gets feet in
           either mapping. */
        var PEDAL_STEPS = [0, 2, 4, 5, 7, 9, 11];
        var PEDAL_FIRST = 29;
        function pedalNote(code) {
            if (pedalBase == null) return null;
            var i = SEQ_INDEX[code];
            if (i == null || i < PEDAL_FIRST) return null;
            return pedalBase + PEDAL_STEPS[i - PEDAL_FIRST];
        }

        /* code → MIDI (before transpose), or null if this key plays nothing. */
        function noteFor(code, shift) {
            var p = pedalNote(code);
            if (p != null) return shift ? sharpAbove(p) : p;
            var m = null;
            if (scheme === 'daw') {
                var off = DAW_OFFSETS[code];
                if (off == null) return null;
                m = base + off;
                /* Shift on a key that is already black has no black key above it. */
                if (shift) return isBlack(m) ? null : sharpAbove(m);
                return m;
            }
            /* sequential */
            var idx = SEQ_INDEX[code];
            if (idx == null || idx >= whites.length) return null;
            m = whites[idx];
            return shift ? sharpAbove(m) : m;
        }

        /* Which physical key plays `midi` right now — used to paint the hints.
           Returns e.g. 'A' or '⇧A' (shifted = black key), or '' for unmapped. */
        function hintFor(midi) {
            if (pedalBase != null) {
                for (var pi = 0; pi < PEDAL_STEPS.length; pi++) {
                    var pm = pedalBase + PEDAL_STEPS[pi];
                    if (pm === midi) return codeLabel(SEQ_CODES[PEDAL_FIRST + pi]);
                    if (sharpAbove(pm) === midi) return '⇧' + codeLabel(SEQ_CODES[PEDAL_FIRST + pi]);
                }
            }
            if (scheme === 'daw') {
                for (var c in DAW_OFFSETS) {
                    if (!DAW_OFFSETS.hasOwnProperty(c)) continue;
                    if (base + DAW_OFFSETS[c] === midi) return codeLabel(c);
                }
                return '';
            }
            var lim = Math.min(whites.length, pedalBase != null ? PEDAL_FIRST : SEQ_CODES.length);
            var wi = whites.indexOf(midi);
            if (wi >= 0 && wi < lim) return codeLabel(SEQ_CODES[wi]);
            /* black key: it is the shifted form of the white key below it */
            if (isBlack(midi)) {
                var below = whites.indexOf(midi - 1);
                if (below >= 0 && below < lim) return '⇧' + codeLabel(SEQ_CODES[below]);
            }
            return '';
        }

        /* Label to paint on a key for a given assist mode. */
        function labelFor(midi, mode) {
            if (mode === 'notes') return noteName(midi);
            if (mode === 'keys') return hintFor(midi);
            return '';
        }

        /* Is this MIDI note reachable from the computer keyboard at all?
           Drives the "you are typing HERE" bracket under the manual. */
        function mapped(midi) { return hintFor(midi) !== ''; }

        /* {manual:{lo,hi}, pedal:{lo,hi}|null} — drives the bar under the keyboard. */
        function mappedSpan() {
            var manual = null;
            if (scheme === 'daw') manual = { lo: base, hi: base + 16 };
            else if (whites.length) {
                var lim = Math.min(whites.length, pedalBase != null ? PEDAL_FIRST : SEQ_CODES.length);
                if (lim > 0) manual = { lo: whites[0], hi: whites[lim - 1] };
            }
            var pedal = (pedalBase == null) ? null
                : { lo: pedalBase, hi: pedalBase + PEDAL_STEPS[PEDAL_STEPS.length - 1] };
            return { manual: manual, pedal: pedal };
        }

        return {
            scheme: function () { return scheme; },
            setScheme: function (s) {
                s = (s === 'sequential') ? 'sequential' : 'daw';
                if (s === scheme) return false;
                scheme = s; return true;
            },
            /* daw only; sequential follows whatever is on screen */
            usesBase: function () { return scheme === 'daw'; },
            base: function () { return base; },
            setBase: function (v) { var p = base; base = clampBase(v); return base !== p; },
            shiftBase: function (d) { return this.setBase(base + d); },
            baseLimits: function () { return { min: baseMin, max: baseMax }; },

            setWhiteKeys: function (list) { whites = list || []; },
            setPedalBase: function (v) { pedalBase = (typeof v === 'number') ? v : null; },
            pedalBase: function () { return pedalBase; },

            noteFor: noteFor,
            hintFor: hintFor,
            labelFor: labelFor,
            mapped: mapped,
            mappedSpan: mappedSpan,

            /* rollover bookkeeping — call from the tool's keydown/keyup */
            press: function (code) {
                if (held[code]) return false;
                held[code] = 1; heldN++;
                if (heldN > maxHeld) maxHeld = heldN;
                return true;
            },
            release: function (code) {
                if (!held[code]) return false;
                delete held[code]; heldN--;
                if (heldN < 0) heldN = 0;
                return true;
            },
            releaseAll: function () { held = {}; heldN = 0; },
            heldCount: function () { return heldN; },
            maxHeld: function () { return maxHeld; },

            noteName: noteName,
            isBlack: isBlack
        };
    }

    w.TimbricaKeymap = {
        create: create,
        SCHEMES: ['daw', 'sequential'],
        DAW_OFFSETS: DAW_OFFSETS,
        SEQ_CODES: SEQ_CODES,
        noteName: noteName,
        isBlack: isBlack
    };
})(window);
